import type { ActionPayload, PhysicsActionId } from "../interaction/action-controller.js";
import type { ChoiceOption, KnowledgeItem, StageDefinition, StepDefinition, StepKind } from "../data/schemas.js";
import type { PhysicsEngine } from "../core/lab-engine.js";
import type { TrialRow } from "../core/store.js";
import type { VariableValue } from "../eom/variable.js";
import { createLabObject, type LabObject } from "../eom/lab-object.js";
import {
  blockHookLocal,
  candleBottomLocal,
  candleTopLocal,
  opaqueObjectTopLocal,
  SCALE_HANDLE_Y,
  scaleHandleLocal,
  SURFACE_LABELS,
} from "../eom/equipment-registry.js";
import { classifyPull, computeNormalForce, type PullState } from "../logic/mechanics-model.js";
import { computePinholeImageHeight, computeShadowHeight } from "../logic/optics-model.js";
import { createSvgRenderer, SVG_NS, type SceneRenderer } from "./svg-renderer.js";
import { bindDragSource } from "../interaction/pointer-controller.js";
import {
  beginForceDrag,
  computeDraggedForce,
  hasExceededDragThreshold,
  isPointInRect,
  type ForceDragSession,
  type Point,
  type Rect,
} from "../interaction/drag-controller.js";

export interface SceneManagerOptions {
  engine: PhysicsEngine;
  root: HTMLElement;
}

/** 冻结出"已完成的 step + 反馈"的原地展示态，替代原本整屏切换的确认屏（067 §M3）。`stage`/`step`
 * 是提交前的 stage/step 快照——submitChoice()/recordTrial()/submitExam() 内部已经同步 advance，
 * `engine.currentStage`/`currentStep` 在反馈展示期间已经指向下一步，不能再用来渲染"刚完成的这步"。
 * `crossStage` 只影响底部按钮文案（"下一步"还是"进入下一个实验"），不影响引擎状态。 */
interface PendingFeedback {
  stage: StageDefinition;
  step: StepDefinition;
  selectedOptionId?: string;
  correct?: boolean | null;
  recordSummary?: string;
  knowledge?: KnowledgeItem;
  crossStage: boolean;
}

function surfaceLabel(id: string): string {
  return SURFACE_LABELS[id] ?? id;
}

const ORIENTATION_LABELS: Record<string, string> = { flat: "平放", vertical: "竖放" };

function orientationLabel(id: string): string {
  return ORIENTATION_LABELS[id] ?? id;
}

/** act 步骤右侧任务 checklist 的文案（todo/064 B/C 节）——按 actionId 派生，不新增 schema 字段。 */
const ACTION_TASK_LABELS: Record<string, string> = {
  select_surface: "选择接触面材质",
  select_orientation: "调整木块朝向",
  add_weight: "增加砝码，改变压力",
  pull_uniform: "匀速拉动，读出测力计示数",
  move_candle: "拖动蜡烛，调整物距",
  move_screen: "拖动光屏，调整距离",
  move_light: "拖动光源，调整光源与遮光物的距离",
  move_shadow_screen: "拖动光屏，调整遮光物到屏的距离",
};

function actionTaskLabel(id: string): string {
  return ACTION_TASK_LABELS[id] ?? id;
}

/** 右侧任务 checklist 当前激活项的操作说明——把原本裸放在场景顶部的引导文案下放到"当前该做什么"
 * 这一具体步骤下面（todo/064 追加修复：用户反馈"任务描述应该在右边/底部，不是实验顶部"）。
 * `pull_uniform` 的说明改用实时 `COACH_TEXT`（见 `coachText()`），随拖拽力度变化，不用静态文案。 */
const ACTION_TASK_DESCRIPTIONS: Record<string, string> = {
  select_surface: "点击下方按钮，选择木块与轨道接触的材质",
  select_orientation: "点击下方按钮，调整木块摆放的朝向",
  add_weight: "把砝码拖到木块上方，或直接点击砝码托盘",
  move_candle: "拖动蜡烛沿轨道移动，或用下方按钮微调",
  move_screen: "拖动光屏沿轨道移动，或用下方按钮微调",
  move_light: "拖动光源沿轨道移动，或用下方按钮微调",
  move_shadow_screen: "拖动光屏沿轨道移动，或用下方按钮微调",
};

function actionTaskDescription(id: string): string {
  if (id === "pull_uniform") return COACH_TEXT.idle;
  return ACTION_TASK_DESCRIPTIONS[id] ?? "";
}

/** 顶部进度胶囊条：九步学习链（`schemas.ts` 的 `StepKind`）每步一个单字缩写，
 * 配合 `title` 属性给出完整中文名（`renderHeader()` 使用）。 */
const STEP_KIND_SHORT: Record<StepKind, string> = {
  guess: "猜",
  design: "设",
  act: "动",
  record: "记",
  pattern: "规",
  knowledge: "知",
  conclude: "结",
  exam: "中",
  summary: "总",
};

const STEP_KIND_LABELS: Record<StepKind, string> = {
  guess: "猜想",
  design: "设计",
  act: "动手",
  record: "记录",
  pattern: "发现规律",
  knowledge: "形成知识点",
  conclude: "我的结论",
  exam: "中考挑战",
  summary: "章节总结",
};

function escapeHtml(text: string): string {
  const map: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
  return text.replace(/[&<>"']/g, (ch) => map[ch] ?? ch);
}

/** 场景世界坐标布局常量（对应 svg viewBox）。 */
const VIEWBOX = "0 0 640 300";
const BOARD_X = 60;
const BOARD_Y = 230;
const BOARD_WIDTH = 460;
const BLOCK_REST_X = 260;
const BLOCK_SLIDE_OFFSET = 46;
const DROP_ZONE: Rect = { x: 200, y: 130, width: 130, height: 80 };
const SCALE_ANCHOR_X = 560;
/** 量程覆盖最坏组合（3 个砝码 + 毛巾，约 19.5N），留够 33% 余量——0.5N 的余量（原 20N 上限）
 * 在 24px 的拉环可视行程里不到 1px，人手拖拽精度和 0.1N 量化步长都覆盖不了，真机反馈"19.1N
 * 木块还没动"就是撞在这条窄余量上（见 drag-controller.ts 的灵敏度标定说明）。 */
const FORCE_MAX = 26;
/** 点击"开始拉动"按钮后，先播放临界力视觉反馈（CSS transition 220ms），再推进步骤——比 220ms 留一点余量。 */
const PULL_FEEDBACK_DELAY_MS = 260;

function blockHeight(orientation: string): number {
  return orientation === "vertical" ? 70 : 40;
}

/**
 * 光学场景（小孔成像/影子，todo/068）布局常量——两个子实验共用同一条水平"光轴/轨道"
 * （`OPTICS_AXIS_X` 是固定中间物的 x 坐标，两端可拖物体各自沿轨道向左/右偏移）。
 * 距离量程 [10,60]cm 与 mission.json 的初始值（30cm）保持同一套约定（M1 report/000 记录过的坑：
 * `VariableTable.set()` 要求变量已 `define`，这里只是渲染层常量，不涉及那条坑）。
 */
const OPTICS_AXIS_X = 320;
const OPTICS_PX_PER_CM = 4;
const OPTICS_MIN_DISTANCE_CM = 10;
const OPTICS_MAX_DISTANCE_CM = 60;
/** 距离拖拽灵敏度：复用 `computeDraggedForce` 的会话状态机，但距离量程（50cm 跨度）远大于力值
 * 量程，按力值标定的 `FORCE_DRAG_SENSITIVITY`（0.06）会让拖满全程也移不了几厘米，这里覆盖成更
 * 大的灵敏度（drag-controller.ts 新增的可选参数，未改变默认行为）。 */
const OPTICS_DRAG_SENSITIVITY = 0.25;
/** 小孔成像组（蜡烛/孔/屏）共用的光轴基准 y 坐标——烛焰（不是蜡烛烛身）纵向居中于此，
 * 孔心也在此，屏幕像标记以此为对称中心展开（呼应 equipment-registry.ts 里三者"共享局部
 * 原点"的约定，以及 `candleTopLocal`/`candleBottomLocal` 卡在烛焰而非烛身的修正）。 */
const PINHOLE_AXIS_Y = 150;
/** 影子组（光源/遮光物/屏）共用的地面基线 y 坐标——光源贴地，遮光物向上生长，屏幕影标记也从
 * 这条线向上展开（不对称，呼应"点光源贴地"的几何前提）。 */
const SHADOW_BASELINE_Y = 250;
/** 派生高度（像高/影高，cm）→ 屏幕标记像素高度的换算系数，并设一个视觉上限，避免极端比例把
 * 标记画出场景外。 */
const MARK_PX_PER_CM = 6;
const MARK_MAX_PX = 95;

/** 光学场景的运行时配置——小孔成像/影子结构完全对称（"两端可拖、中间固定"），用同一套渲染/
 * 拖拽逻辑参数化两个子实验，避免抄两份几乎相同的方法（`scene-manager.ts` 只增不改的前提下，
 * 这是本文件里两个新场景之间唯一共享的抽象，不是提前泛化——两个实例现在就都存在）。 */
interface OpticsSceneConfig {
  kind: "pinhole" | "shadow";
  leftType: string;
  rightType: string;
  fixedType: string;
  leftVar: string;
  rightVar: string;
  derivedVar: string;
  leftAction: PhysicsActionId;
  rightAction: PhysicsActionId;
  markKind: "inverted" | "upright";
  axisY: number;
  leftLabel: string;
  rightLabel: string;
  /** 拖拽实时读数条的简短标签（`data-optics-live` 那一行）——用具体变量名（"物距"/"像距"）
   * 替代此前写死的通用词"当前"，拖哪端就显示哪端对应的简短名，默认（未拖拽时）显示
   * `leftShortLabel`。与 `leftLabel`/`rightLabel` 分开维护，因为后者是给按钮用的完整器材名
   * + 括注，直接摘取括注文字会是脆弱的字符串解析。 */
  leftShortLabel: string;
  rightShortLabel: string;
  derivedLabel: string;
  compute: (leftCm: number, rightCm: number) => number;
}

const PINHOLE_CONFIG: OpticsSceneConfig = {
  kind: "pinhole",
  leftType: "candle",
  rightType: "screen",
  fixedType: "pinhole-board",
  leftVar: "objectDistance",
  rightVar: "screenDistance",
  derivedVar: "imageHeight",
  leftAction: "move_candle",
  rightAction: "move_screen",
  markKind: "inverted",
  axisY: PINHOLE_AXIS_Y,
  leftLabel: "蜡烛（物距）",
  rightLabel: "光屏（像距）",
  leftShortLabel: "物距",
  rightShortLabel: "像距",
  derivedLabel: "像高",
  compute: computePinholeImageHeight,
};

const SHADOW_CONFIG: OpticsSceneConfig = {
  kind: "shadow",
  leftType: "point-light",
  rightType: "screen",
  fixedType: "opaque-object",
  leftVar: "lightObjectDistance",
  rightVar: "objectScreenDistance",
  derivedVar: "shadowHeight",
  leftAction: "move_light",
  rightAction: "move_shadow_screen",
  markKind: "upright",
  axisY: SHADOW_BASELINE_Y,
  leftLabel: "光源（光源距物）",
  rightLabel: "光屏（物距屏）",
  leftShortLabel: "光源距物",
  rightShortLabel: "物距屏",
  derivedLabel: "影高",
  compute: computeShadowHeight,
};

function opticsVarForAction(actionId: PhysicsActionId): string | undefined {
  if (actionId === PINHOLE_CONFIG.leftAction) return PINHOLE_CONFIG.leftVar;
  if (actionId === PINHOLE_CONFIG.rightAction) return PINHOLE_CONFIG.rightVar;
  if (actionId === SHADOW_CONFIG.leftAction) return SHADOW_CONFIG.leftVar;
  if (actionId === SHADOW_CONFIG.rightAction) return SHADOW_CONFIG.rightVar;
  return undefined;
}

type CoachKey =
  | "idle"
  | "static"
  | "breaking"
  | "starting"
  | "uniform"
  | "accelerating"
  | "decelerating"
  | "blocked-range";

const COACH_TEXT: Record<CoachKey, string> = {
  idle: "轻轻拖动测力计的黄色拉环，向右拉，对木块施力。",
  static: "木块还没动——静摩擦力等于此刻的拉力。",
  breaking: "快到临界点了，再加一点力就能拉动木块。",
  starting: "木块起步了！稍减小一点拉力，把示数稳住。",
  uniform: "木块在匀速滑动，示数稳定——松手，记下此刻读数。",
  accelerating: "拉力偏大，木块在加速；回退一点，让示数落到匀速区间。",
  decelerating: "拉力偏小，木块在减速；加一点力，让示数落到匀速区间。",
  "blocked-range": "已到量程上限；请松手，重新缓慢拉到匀速区间。",
};

/**
 * act 步骤的场景渲染壳：真实 SVG 器材（木块/接触面/砝码/测力计）+ 拖拽交互
 * （tech/108 §13）。guess/design/record/pattern/knowledge/conclude 六类步骤维持纯 HTML 面板不变。
 * 只依赖 PhysicsEngine 的公开 API + 事件总线，不感知具体实验内容——同一份代码换任意
 * ExperimentPack 都能跑（器材几何在 `eom/equipment-registry.ts`，与实验内容无关）。
 */
export class SceneManager {
  private readonly engine: PhysicsEngine;
  private readonly root: HTMLElement;
  private activeKnowledge: KnowledgeItem | undefined;
  private blockedNotice = false;
  private examResult = "";
  private completed = false;
  /** submitChoice()/recordTrial() 内部会同步 emit `step.started`——置位后，mount() 的监听器
   * 只做状态收尾、不触发 render()，把"渲染下一步"的决定权交回点击处理器（先展示反馈确认屏，
   * 真机第六轮反馈"回答后没有确认对错就直接跳下一题"）。 */
  private awaitingConfirm = false;
  /** 答错时 triggerHint() 同步设置的知识卡——不能直接读 activeKnowledge，因为同一次
   * submitChoice() 调用里紧接着的 step.started 会把它清空（这正是之前"错误反馈从未渲染过"的
   * 隐藏 bug），这里单独存一份供确认屏展示。 */
  private confirmKnowledge: KnowledgeItem | undefined;
  /** 提交后待确认的原地反馈——非 undefined 时 render() 冻结在这份快照上，不读 engine 的当前状态。 */
  private pendingFeedback: PendingFeedback | undefined;

  private svgRenderer: SceneRenderer | undefined;
  private appliedForce = 0;
  /** 本次拖拽中木块是否已经起步（拉力曾超过最大静摩擦）。起步后摩擦从静摩擦切换到滑动摩擦，
   *  匀速判定必须知道这个"已起步"状态，否则松手瞬间的拉力会被误判成静摩擦阶段。 */
  private pullStarted = false;
  private unbindDrags: Array<() => void> = [];
  private connectorLine: SVGLineElement | undefined;
  private connectorDots: { from: SVGCircleElement; to: SVGCircleElement } | undefined;
  /** 光学场景（todo/068）当前用的是哪套子实验配置——由最近一次 `mountOpticsActScene()` 写入，
   * record/pattern/conclude 的回顾场景只读它、不重新判断（这些 step kind 没有 requiredActions，
   * 无法从 step 本身推断），mission 状态机保证 act 总在同一 stage 的 record/conclude 之前跑过。 */
  private opticsSceneKind: "pinhole" | "shadow" = "pinhole";
  private opticsRays: SVGLineElement[] = [];

  constructor(options: SceneManagerOptions) {
    this.engine = options.engine;
    this.root = options.root;
  }

  mount(): void {
    this.injectStyles();
    this.engine.eventBus.on("step.started", () => {
      if (this.awaitingConfirm) {
        // 捕获"答错"知识卡供确认屏展示，避免被这里的清空覆盖；不渲染——留给确认屏的
        // "下一步"按钮触发真正的 render()（见 bindEvents() 的 submit-choice/record 处理器）。
        this.confirmKnowledge = this.activeKnowledge;
        this.activeKnowledge = undefined;
        this.blockedNotice = false;
        this.examResult = "";
        return;
      }
      this.activeKnowledge = undefined;
      this.blockedNotice = false;
      this.examResult = "";
      this.render();
    });
    this.engine.eventBus.on("step.blocked", () => {
      this.blockedNotice = true;
      this.render();
    });
    this.engine.eventBus.on("knowledge.shown", (event) => {
      const payload = event.payload as { knowledgeId: string };
      const item = this.engine.pack?.knowledge.find((entry) => entry.id === payload.knowledgeId);
      if (item) this.activeKnowledge = item;
    });
    this.engine.eventBus.on("experiment.completed", () => {
      this.completed = true;
      this.render();
    });
  }

  private render(): void {
    this.unbindDrags.forEach((unbind) => unbind());
    this.unbindDrags = [];
    this.svgRenderer = undefined;
    this.connectorLine = undefined;
    this.connectorDots = undefined;
    this.opticsRays = [];

    if (this.completed) {
      this.root.innerHTML = this.renderCompletion();
      return;
    }
    // pendingFeedback 存在时，整个壳子渲染的是"刚提交的那个 step"的冻结快照,而不是
    // engine 当前（已经同步 advance 过）的 step——反馈横幅由 renderFeedbackBanner() 追加在末尾
    // （067 §M3，替代原本整屏切换的确认屏）。
    const feedback = this.pendingFeedback;
    const stage = feedback ? feedback.stage : this.engine.currentStage;
    const step = feedback ? feedback.step : this.engine.currentStep;
    this.root.innerHTML = `
      <div class="pe-shell${step.kind === "act" ? " pe-shell--act" : ""}">
        ${this.renderHeader(stage, step)}
        <section class="pe-question-panel">
          <p class="pe-badge pe-badge-question">🔍 探究问题</p>
          <h2 class="pe-question">${escapeHtml(stage.question)}</h2>
          ${
            // act 步骤的"本步任务"和右侧 checklist（renderTaskChecklist()）说的是同一件事，
            // 两处并存是真实的文字重复（追加修复：用户核实 docs/111/010 的评审意见后确认）。
            step.kind === "act"
              ? ""
              : `<div class="pe-step-meta">
                  <p class="pe-badge pe-badge-task">📌 本步任务</p>
                  <p class="pe-prompt">${escapeHtml(step.prompt)}</p>
                </div>`
          }
        </section>
        <section class="pe-step">
          ${this.renderStepBody(step, feedback)}
        </section>
        ${!feedback && this.blockedNotice ? `<p class="pe-blocked">${this.blockedMessage(step)}</p>` : ""}
        ${
          // conclude 的知识卡不在选项之前展示（P0-4：提前展示会泄露答案），作答后由
          // renderFeedbackBanner() 的 feedback.knowledge 承接；这里只对非 conclude 的
          // 步骤（guess/design/pattern 等带 knowledgeRefs 的）在选项下方追加一张卡。
          !feedback && this.activeKnowledge && step.kind !== "knowledge" && step.kind !== "conclude" ? this.renderKnowledgeCard(this.activeKnowledge) : ""
        }
        ${
          // conclude 没有独立的"需要提示"按钮——知识点作答后再展示，"提示"会提前泄露答案。
          !feedback && step.knowledgeRefs?.length && step.kind !== "knowledge" && step.kind !== "conclude" ? `<button class="pe-hint" data-action="hint">需要提示</button>` : ""
        }
        ${feedback ? this.renderFeedbackBanner(feedback) : ""}
      </div>
    `;
    this.bindEvents();
    if (!feedback && step.kind === "act") {
      if (this.isOptics()) this.mountOpticsActScene(step);
      else this.mountActScene(step);
    }
    if (step.kind === "record" || step.kind === "pattern" || step.kind === "conclude") {
      if (this.isOptics()) this.mountOpticsRecapScene();
      else this.mountRecapScene();
    }
  }

  /** 本实验包是否是光学（小孔成像/影子，todo/068）——`render()`/`renderStepBody()` 靠它分派到
   * `renderOptics*`/`mountOptics*` 兄弟方法，不触碰任何既有摩擦力方法体。 */
  private isOptics(): boolean {
    return this.engine.pack?.experiment.subject === "optics";
  }

  /** 顶部两级进度:「实验 X/Y」文字 + 当前实验九步链的胶囊条(已完成打勾/当前放大/未来灰色),
   * 胶囊数量按 `stage.steps` 实际长度生成,不写死 9(章节总结 Stage 的步骤更少,自动适配)。 */
  private renderHeader(stage: StageDefinition, step: StepDefinition): string {
    const stages = this.engine.pack?.mission.stages ?? [];
    const stageIndex = stages.findIndex((entry) => entry.id === stage.id);
    const experimentCount = stages.filter((entry) => entry.kind !== "summary").length;
    const label = stage.kind === "summary" ? "📚 本章总结" : `实验 ${stageIndex + 1} / ${experimentCount}`;

    const stepIndex = stage.steps.findIndex((entry) => entry.id === step.id);
    const pills = stage.steps
      .map((entry, i) => {
        const state = i < stepIndex ? "done" : i === stepIndex ? "current" : "upcoming";
        const symbol = i < stepIndex ? "✓" : STEP_KIND_SHORT[entry.kind];
        return `<span class="pe-pill pe-pill--${state}" title="${escapeHtml(STEP_KIND_LABELS[entry.kind])}">${symbol}</span>`;
      })
      .join("");

    return `
      <div class="pe-header">
        <span class="pe-header-label">${escapeHtml(label)}</span>
        <div class="pe-pills" role="list" aria-label="学习步骤进度">${pills}</div>
      </div>
    `;
  }

  /** blocked 提示按步骤类型区分——record 步骤的"上面操作"字面上不存在（回顾场景只读），
   * 原样复用 act 步骤的通用文案会误导用户去点一个点不动的画面（真机反馈"无法实现，无法记录第二组"）。 */
  private blockedMessage(step: StepDefinition): string {
    if (step.kind === "record") {
      const stageId = this.engine.currentStage.id;
      const required = step.requiredTrials ?? 1;
      const remaining = Math.max(0, required - this.engine.store.trialCount(stageId));
      return `⚠️ 还差 ${remaining} 组：可以直接再次点击「记录当前数据」重复记录，或点击下方「返回操作台，调整条件」测出真正不同的一组数据。`;
    }
    return "⚠️ 还差一步：请先完成上面的操作。";
  }

  private renderStepBody(step: StepDefinition, feedback?: PendingFeedback): string {
    switch (step.kind) {
      case "guess":
      case "design":
        return this.renderChoiceOptions(step.options ?? [], "submit-choice", feedback);
      case "pattern":
        return `${this.renderRecapContainer()}${this.renderChoiceOptions(step.options ?? [], "submit-choice", feedback)}`;
      case "conclude": {
        // P0-4：知识点不再在选项之前展示（否则提前泄露答案）。作答后进入反馈冻结态时，
        // 知识卡由 renderFeedbackBanner() 的 feedback.knowledge 承接（答错走 hintOnWrongAnswer、
        // 答对走 enterStep 时 hintOnStepEntered 置位后转存进 confirmKnowledge）。
        const conclude = this.renderChoiceOptions(step.options ?? [], "submit-choice", feedback);
        const exam = step.exam
          ? `
          <div class="pe-exam">
            <h4>🎯 中考挑战</h4>
            <p>${escapeHtml(step.exam.prompt)}</p>
            ${this.renderChoiceOptions(step.exam.options, "submit-exam", feedback)}
            ${this.examResult ? `<p class="pe-exam-result">${escapeHtml(this.examResult)}</p>` : ""}
          </div>
        `
          : "";
        return `${this.renderRecapContainer()}${conclude}${exam}`;
      }
      case "act":
        return this.isOptics() ? this.renderOpticsActScene(step) : this.renderActScene(step);
      case "record": {
        // 反馈冻结态渲染的是提交前的 stage——crossStage 场景下 engine.currentStage 已经变了，
        // 不能用来查这个 step 的历史记录（067 §M3）。
        const stageId = feedback ? feedback.stage.id : this.engine.currentStage.id;
        return `${this.renderRecapContainer()}${this.renderRecordControls(step, stageId, feedback)}`;
      }
      case "summary":
        return this.renderSummary(step);
      case "exam":
        return this.renderExamStep(step, feedback);
      case "knowledge":
        return this.renderKnowledgeStep(step);
      default:
        return "";
    }
  }

  /** `feedback` 存在时进入冻结展示态：所有按钮禁用，选中项按对/错/中性分别标出颜色
   * （067 §M3）。省略 `feedback` 时渲染与改造前完全一致，零视觉/测试回归。 */
  private renderChoiceOptions(options: ChoiceOption[], action: string, feedback?: PendingFeedback): string {
    return `<div class="pe-options">${options
      .map((option) => {
        if (!feedback) return `<button data-action="${action}" data-option-id="${option.id}">${escapeHtml(option.label)}</button>`;
        const isSelected = feedback.selectedOptionId === option.id;
        const resultClass = !isSelected
          ? ""
          : feedback.correct === false
            ? " pe-option--wrong"
            : feedback.correct === true
              ? " pe-option--correct"
              : " pe-option--selected";
        return `<button data-action="${action}" data-option-id="${option.id}" disabled class="pe-option${resultClass}">${escapeHtml(option.label)}</button>`;
      })
      .join("")}</div>`;
  }

  /** summary 步骤：把 summaryItems 渲染成"标题 + 要点列表"的知识体系总结，尾部给一个"继续"按钮。 */
  private renderSummary(step: StepDefinition): string {
    const items = step.summaryItems ?? [];
    const list = items
      .map(
        (item) => `
        <div class="pe-summary-item">
          <h4>${escapeHtml(item.title)}</h4>
          <ul>${item.points.map((point) => `<li>${escapeHtml(point)}</li>`).join("")}</ul>
        </div>
      `,
      )
      .join("");
    return `${list}<button data-action="submit-summary">继续</button>`;
  }

  /** exam 步骤：每个子实验收尾的独立一步（区别于 conclude 步骤底部的附加中考题）。标题按
   * stage.kind 区分——只有真正的整章总结 stage 才叫"章节末"，其余子实验的收尾叫"本节"，
   * 避免用户在 1/5 就看到"章节末"字样误判进度（追加修复：docs/111/010 评审后用户确认）。 */
  private renderExamStep(step: StepDefinition, feedback?: PendingFeedback): string {
    const exam = step.exam;
    if (!exam) return "";
    // 反馈冻结态下 engine.currentStage 已经 advance 到下一个 stage，标题必须按提交前的
    // stage.kind 判断，否则章节末/本节的措辞会在反馈横幅显示期间读到错误的 stage（067 §M3）。
    const stageKind = feedback ? feedback.stage.kind : this.engine.currentStage.kind;
    const heading = stageKind === "summary" ? "🎯 章节末综合中考" : "🎯 本节中考挑战";
    return `
      <div class="pe-exam">
        <h4>${heading}</h4>
        <p>${escapeHtml(exam.prompt)}</p>
        ${this.renderChoiceOptions(exam.options, "submit-exam-step", feedback)}
      </div>
    `;
  }

  /** knowledge 步骤（⑥ 形成知识点）：内联展示知识卡 + "继续"按钮推进（无对错、无门槛）。 */
  private renderKnowledgeStep(step: StepDefinition): string {
    const knowledgeId = step.knowledgeRefs?.[0];
    const item = knowledgeId
      ? this.engine.pack?.knowledge.find((entry) => entry.id === knowledgeId)
      : undefined;
    const card = item ? `<div class="pe-knowledge">${this.renderKnowledgeBody(item)}</div>` : "";
    return `${card}<button data-action="submit-knowledge">继续</button>`;
  }

  /** act 步骤的 HTML 骨架：左「实验工作台」（场景+提示+按钮）+ 右「实验任务」checklist 两栏
   * （todo/064 方案 B），只对 act 步骤生效，其余 step kind 不受影响。真实 SVG 场景 + 拖拽热点 +
   * 离散选择按钮（材质/朝向不必强做拖拽）。"开始拉动"降级为场景下方的低调备选路径
   * （todo/064 方案 D：可达性兜底，不再是与拖拽并列的默认操作）。 */
  private renderActScene(step: StepDefinition): string {
    const actions = step.requiredActions ?? [];
    const controls: string[] = [];
    if (actions.includes("select_surface")) {
      for (const surfaceId of ["glass", "wood", "towel"]) {
        controls.push(
          `<button data-action="perform" data-action-id="select_surface" data-surface="${surfaceId}">${surfaceLabel(surfaceId)}</button>`,
        );
      }
    }
    if (actions.includes("select_orientation")) {
      for (const orientationId of ["flat", "vertical"]) {
        controls.push(
          `<button data-action="perform" data-action-id="select_orientation" data-orientation="${orientationId}">${orientationLabel(orientationId)}</button>`,
        );
      }
    }

    const friction = this.engine.store.variables.get("frictionReading")?.value;
    const readout = friction != null ? `<p class="pe-readout">测力计：${friction} N</p>` : "";
    const fallbackPull = actions.includes("pull_uniform")
      ? `<button class="pe-fallback-pull" data-action="perform" data-action-id="pull_uniform">无法拖拽？点击模拟拉动</button>`
      : "";
    const forceLive = actions.includes("pull_uniform")
      ? `<p class="pe-force-live" data-force-readout>当前拉力：<strong>0.0</strong> N</p>`
      : "";

    return `
      <div class="pe-act-layout">
        <div class="pe-lab-col">
          <div class="pe-scene-wrap">
            <svg class="pe-scene" viewBox="${VIEWBOX}" data-scene></svg>
            ${actions.includes("add_weight") ? `<div class="pe-weight-tray" data-drag-weight title="拖到木块上方，或直接点击">➕ 砝码（拖到木块上或点击）</div>` : ""}
            <div class="pe-actions">${controls.join("")}</div>
            ${fallbackPull}
          </div>
          <div class="pe-result-row">
            ${forceLive}
            ${readout}
          </div>
        </div>
        <aside class="pe-task-col">
          <h4 class="pe-task-col-title">实验任务</h4>
          <ol class="pe-task-checklist" data-task-checklist>${this.renderTaskChecklist(step)}</ol>
        </aside>
      </div>
    `;
  }

  /** 右侧任务 checklist：直接从 `step.requiredActions` 派生条目，状态用已有的
   * `store.actionCount(stepId, actionId)`（`core/store.ts`）判定 done/active/pending，
   * 零 schema 改动、零内容包改动（todo/064 方案 C）。每次 `performAction()` 之后调用方都会走
   * 一次完整 `render()`，checklist 随之用最新 `actionCount` 重新生成，不需要单独的刷新钩子。
   * 当前激活项额外带一行操作说明（追加修复：任务名称/操作说明/实验结果三层，说明跟着"当前该做
   * 什么"走，不再裸放在场景顶部）；`pull_uniform` 项的说明段落挂 `data-coach`，拖拽时
   * `updateForceVisual()` 继续用同一个属性选择器实时改写文案，选择器不变、只挪了位置。 */
  private renderTaskChecklist(step: StepDefinition): string {
    const actions = step.requiredActions ?? [];
    const firstPendingIndex = actions.findIndex((id) => this.engine.store.actionCount(step.id, id) === 0);
    return actions
      .map((id, index) => {
        const done = this.engine.store.actionCount(step.id, id) > 0;
        const isActive = !done && index === firstPendingIndex;
        const state = done ? "done" : isActive ? "active" : "pending";
        const marker = done ? "✓" : String(index + 1);
        const desc = isActive ? actionTaskDescription(id) : "";
        const descAttr = id === "pull_uniform" ? " data-coach" : "";
        return `<li class="pe-task-item pe-task-item--${state}">
          <span class="pe-task-num">${marker}</span>
          <div class="pe-task-body">
            <span class="pe-task-label">${escapeHtml(actionTaskLabel(id))}</span>
            ${desc ? `<p class="pe-task-desc"${descAttr}>${desc}</p>` : ""}
          </div>
        </li>`;
      })
      .join("");
  }

  /** 只读回顾场景容器：record/conclude 步骤复现刚才的实验状态，解决"填数据表看不到实验"的割裂感。 */
  private renderRecapContainer(): string {
    return `
      <div class="pe-scene-wrap pe-scene-wrap--recap">
        <p class="pe-recap-caption">刚才的实验状态</p>
        <svg class="pe-scene pe-scene-readonly" viewBox="${VIEWBOX}" data-scene-recap></svg>
      </div>
    `;
  }

  /** 首次挂载/每次进入 act 步骤：读当前变量建 LabObject，挂 SvgRenderer，绑拖拽热点。 */
  private mountActScene(step: StepDefinition): void {
    const svg = this.root.querySelector<SVGSVGElement>("svg[data-scene]");
    if (!svg) return;
    this.appliedForce = 0;
    this.pullStarted = false;
    this.svgRenderer = createSvgRenderer(svg);
    this.refreshSceneObjects();

    // 木块与测力计必然同时存在于 act 场景——绳子不按 actions 判断，无条件挂上
    // （真机反馈"测力计和木块没有任何连线，怎么拉的没有具象"）。
    this.connectorLine = this.appendConnector(svg, BLOCK_REST_X);

    const actions = step.requiredActions ?? [];
    if (actions.includes("add_weight")) {
      // 放置区必须在 render() 清空过 svg 之后再挂——render() 会 replaceChildren()，
      // 挂载前写死的 <rect> 会被整个清空（真机反馈"无法拖拽"的确凿根因）。
      const dropZone = this.appendDropZone(svg);
      const tray = this.root.querySelector<HTMLElement>("[data-drag-weight]");
      if (tray) this.bindWeightDrag(tray, svg, dropZone);
    }
    if (actions.includes("pull_uniform")) {
      const handle = svg.querySelector<SVGElement>('[data-handle="force"]');
      if (handle) this.bindForceDrag(handle, svg);
    }
  }

  /** 只读快照：从当前 PhysicsStore 变量算出 LabObject[]，不依赖 svgRenderer/store 写入——act 场景与
   * record/conclude 回顾场景共用同一份计算逻辑，保证两处画出来的器材状态一致。`blockX` 必须和
   * 绳子端点用的是同一个值（调用方传入同一次 `currentBlockX()` 求值结果）——否则木块画在一处、
   * 绳子焊在另一处，回顾场景就会看起来"断开"（真机反馈"测力计的线和木块的线又断了"的确凿根因）。 */
  private buildObjectsSnapshot(blockX: number = BLOCK_REST_X): LabObject[] {
    const surface = String(this.engine.store.variables.get("surface")?.value ?? "wood");
    const orientation = String(this.engine.store.variables.get("orientation")?.value ?? "flat");
    const weightCount = Number(this.engine.store.variables.get("weightCount")?.value ?? 0);
    const height = blockHeight(orientation);

    const board = createLabObject("board", "board", { x: BOARD_X, y: BOARD_Y }, { material: surface, length: BOARD_WIDTH });
    const block = createLabObject("block", "block", { x: blockX, y: BOARD_Y }, { orientation });
    const weights: LabObject[] = [];
    for (let i = 0; i < weightCount; i++) {
      weights.push(createLabObject(`weight-${i}`, "weight", { x: blockX + 18, y: BOARD_Y - height }, { stackIndex: i }));
    }
    const scale = createLabObject(
      "spring-scale",
      "spring-scale",
      // 拉环（局部 y = SCALE_HANDLE_Y）必须与木块挂钩（世界 y = BOARD_Y - height/2）水平对齐，
      // 因此测力计原点要上移 SCALE_HANDLE_Y，否则绳子会向下倾斜 9px（P0-3：连接不水平）。
      { x: SCALE_ANCHOR_X, y: BOARD_Y - height / 2 - SCALE_HANDLE_Y },
      { appliedForce: this.appliedForce, forceMax: FORCE_MAX },
    );
    return [board, block, ...weights, scale];
  }

  /** 从当前 PhysicsStore 变量重建场景里的 LabObject 快照（材质/朝向/砝码数变了都靠这个刷新）。 */
  private refreshSceneObjects(): void {
    if (!this.svgRenderer) return;
    const blockX = this.currentBlockX();
    const objects = this.buildObjectsSnapshot(blockX);
    for (const object of objects) this.engine.store.putObject(object);
    this.svgRenderer.render(objects);
  }

  /** record/conclude 步骤的只读回顾：一次性画出当前状态，不绑拖拽、不写 store，纯展示。木块和绳子
   * 端点共用同一个 `blockX`（同一次求值），保证两者永远焊在一起，不会各算各的。 */
  private mountRecapScene(): void {
    const svg = this.root.querySelector<SVGSVGElement>("svg[data-scene-recap]");
    if (!svg) return;
    const blockX = this.currentBlockX();
    createSvgRenderer(svg).render(this.buildObjectsSnapshot(blockX));
    // 绳子端点用离开 act 步骤时最后的力值/木块位置——顺带让回顾页也能看到"绳子拉直了"，
    // 呼应上一轮"看不到实验"的诉求，不额外存引用（只读快照，不需要后续更新）。
    this.appendConnector(svg, blockX);
  }

  /** 根据 act 步骤的 `requiredActions` 判断当前是小孔成像还是影子子实验——两者共用同一个
   * `subject:"optics"` 实验包，唯一能在渲染时区分"现在是哪个子实验"的信号就是这一步要求的
   * action id（`design_own` 复用 `move_light`，天然归入影子配置，符合它"研究光源远近影响影长"
   * 的内容设计）。 */
  private opticsConfigForStep(step: StepDefinition): OpticsSceneConfig {
    const actions = step.requiredActions ?? [];
    return actions.includes(SHADOW_CONFIG.leftAction) || actions.includes(SHADOW_CONFIG.rightAction)
      ? SHADOW_CONFIG
      : PINHOLE_CONFIG;
  }

  /** 光学 act 步骤的 HTML 骨架——镜像 `renderActScene()` 的两栏布局（左场景+右任务清单），
   * 但没有材质/朝向/砝码这类离散选择控件，取而代之的是两个可拖端点 + `±5cm` 步进按钮兜底
   * （呼应 `renderActScene()` 里"开始拉动"降级为低调备选路径的同一条可达性原则）。 */
  private renderOpticsActScene(step: StepDefinition): string {
    const config = this.opticsConfigForStep(step);
    const actions = step.requiredActions ?? [];
    const derivedValue = this.engine.store.variables.get(config.derivedVar)?.value;
    const readout =
      derivedValue != null ? `<p class="pe-readout">${config.derivedLabel}：${Number(derivedValue).toFixed(1)} cm</p>` : "";

    const steppers: string[] = [];
    if (actions.includes(config.leftAction)) steppers.push(this.renderOpticsStepper(config.leftAction, config.leftLabel));
    if (actions.includes(config.rightAction)) steppers.push(this.renderOpticsStepper(config.rightAction, config.rightLabel));

    return `
      <div class="pe-act-layout">
        <div class="pe-lab-col">
          <div class="pe-scene-wrap">
            <svg class="pe-scene" viewBox="${VIEWBOX}" data-scene></svg>
            <div class="pe-optics-fallback">${steppers.join("")}</div>
          </div>
          <div class="pe-result-row">
            <p class="pe-force-live" data-optics-live><span data-optics-live-label>${config.leftShortLabel}</span>：<strong>--</strong> cm</p>
            ${readout}
          </div>
        </div>
        <aside class="pe-task-col">
          <h4 class="pe-task-col-title">实验任务</h4>
          <ol class="pe-task-checklist" data-task-checklist>${this.renderTaskChecklist(step)}</ol>
        </aside>
      </div>
    `;
  }

  private renderOpticsStepper(actionId: PhysicsActionId, label: string): string {
    return `
      <div class="pe-optics-stepper">
        <span>${escapeHtml(label)}</span>
        <button class="pe-fallback-pull" data-action="optics-step" data-action-id="${actionId}" data-delta="-5">−5cm</button>
        <button class="pe-fallback-pull" data-action="optics-step" data-action-id="${actionId}" data-delta="5">+5cm</button>
      </div>
    `;
  }

  /** 光学场景的 LabObject 快照——两端可拖对象 + 一个固定中间物，三者共享同一条 `axisY`，
   * 只在 x 轴上按距离变量错开（呼应 equipment-registry.ts 里"共享局部原点"的坐标约定）。
   * `buildObjectsSnapshot()`（摩擦力）与本方法互不调用，纯粹是同名兄弟。 */
  private buildOpticsSnapshot(config: OpticsSceneConfig, leftCm: number, rightCm: number): LabObject[] {
    const leftX = OPTICS_AXIS_X - leftCm * OPTICS_PX_PER_CM;
    const rightX = OPTICS_AXIS_X + rightCm * OPTICS_PX_PER_CM;
    const derivedCm = config.compute(leftCm, rightCm);
    const markHeightPx = Math.min(MARK_MAX_PX, derivedCm * MARK_PX_PER_CM);
    const left = createLabObject("optics-left", config.leftType, { x: leftX, y: config.axisY }, {});
    const fixed = createLabObject("optics-fixed", config.fixedType, { x: OPTICS_AXIS_X, y: config.axisY }, {});
    const right = createLabObject(
      "optics-right",
      config.rightType,
      { x: rightX, y: config.axisY },
      { markHeightPx, markKind: config.markKind },
    );
    return [left, fixed, right];
  }

  private opticsLeftRightCm(config: OpticsSceneConfig): { leftCm: number; rightCm: number } {
    const leftCm = Number(this.engine.store.variables.get(config.leftVar)?.value ?? OPTICS_MIN_DISTANCE_CM);
    const rightCm = Number(this.engine.store.variables.get(config.rightVar)?.value ?? OPTICS_MIN_DISTANCE_CM);
    return { leftCm, rightCm };
  }

  private refreshOpticsSceneObjects(config: OpticsSceneConfig): void {
    if (!this.svgRenderer) return;
    const { leftCm, rightCm } = this.opticsLeftRightCm(config);
    const objects = this.buildOpticsSnapshot(config, leftCm, rightCm);
    for (const object of objects) this.engine.store.putObject(object);
    this.svgRenderer.render(objects);
  }

  /** 首次挂载/每次进入光学 act 步骤：建场景对象、画光线、按 requiredActions 绑两端拖拽。
   * 镜像 `mountActScene()` 的结构，但不碰它的方法体。 */
  private mountOpticsActScene(step: StepDefinition): void {
    const svg = this.root.querySelector<SVGSVGElement>("svg[data-scene]");
    if (!svg) return;
    const config = this.opticsConfigForStep(step);
    this.opticsSceneKind = config.kind;
    this.svgRenderer = createSvgRenderer(svg);
    this.refreshOpticsSceneObjects(config);
    this.mountOpticsRays(svg, config);

    const actions = step.requiredActions ?? [];
    if (actions.includes(config.leftAction)) {
      const handle = svg.querySelector<SVGElement>('[data-object-id="optics-left"] [data-handle]');
      if (handle) this.bindOpticsDrag(handle, svg, config, "left");
    }
    if (actions.includes(config.rightAction)) {
      const handle = svg.querySelector<SVGElement>('[data-object-id="optics-right"] [data-handle]');
      if (handle) this.bindOpticsDrag(handle, svg, config, "right");
    }
  }

  /** record/pattern/conclude 步骤的只读光学回顾——用最近一次 act 步骤记下的 `opticsSceneKind`
   * 选配置（这几个 step kind 没有 requiredActions，无法自行判断）。镜像 `mountRecapScene()`。 */
  private mountOpticsRecapScene(): void {
    const svg = this.root.querySelector<SVGSVGElement>("svg[data-scene-recap]");
    if (!svg) return;
    const config = this.opticsSceneKind === "shadow" ? SHADOW_CONFIG : PINHOLE_CONFIG;
    const { leftCm, rightCm } = this.opticsLeftRightCm(config);
    createSvgRenderer(svg).render(this.buildOpticsSnapshot(config, leftCm, rightCm));
    this.mountOpticsRays(svg, config);
  }

  /** 追加光线示意线（仿 `appendConnector()` 的"渲染后追加"模式）：小孔成像画两条交叉光线
   * （烛顶经孔到像底、烛底经孔到像顶，直观展示倒立成因）；影子画一条光源经遮光物顶端到影子
   * 边界的光线。用两段直线在枢轴点相接，而不是求一条数学上连续的直线——避免让
   * equipment-registry.ts 的固定像素常量和这里的距离换算系数强耦合到必须联立求解才能保证共线
   * （todo/068 方案 §设计决策），对示意图而言两段直线视觉上已经足够直观。 */
  private mountOpticsRays(svg: SVGSVGElement, config: OpticsSceneConfig): void {
    const count = config.kind === "pinhole" ? 4 : 2;
    this.opticsRays = [];
    for (let i = 0; i < count; i++) {
      const line = document.createElementNS(SVG_NS, "line") as SVGLineElement;
      line.setAttribute("class", "pe-ray");
      line.setAttribute("data-ray", "");
      svg.appendChild(line);
      this.opticsRays.push(line);
    }
    const { leftCm, rightCm } = this.opticsLeftRightCm(config);
    this.updateOpticsRays(config, this.buildOpticsSnapshot(config, leftCm, rightCm));
  }

  private setRaySegment(line: SVGLineElement, from: Point, to: Point): void {
    line.setAttribute("x1", String(from.x));
    line.setAttribute("y1", String(from.y));
    line.setAttribute("x2", String(to.x));
    line.setAttribute("y2", String(to.y));
  }

  private updateOpticsRays(config: OpticsSceneConfig, objects: LabObject[]): void {
    if (this.opticsRays.length === 0) return;
    const [left, fixed, right] = objects;
    const markHeightPx = Number(right.state.markHeightPx ?? 0);
    if (config.kind === "pinhole") {
      const top = candleTopLocal();
      const bottom = candleBottomLocal();
      // 光线一：烛顶 → 孔 → 像底（倒立：物体上方的光线穿过孔后落到屏幕下方）
      this.setRaySegment(this.opticsRays[0], { x: left.pose.x + top.x, y: left.pose.y + top.y }, { x: fixed.pose.x, y: fixed.pose.y });
      this.setRaySegment(this.opticsRays[1], { x: fixed.pose.x, y: fixed.pose.y }, { x: right.pose.x, y: right.pose.y + markHeightPx / 2 });
      // 光线二：烛底 → 孔 → 像顶
      this.setRaySegment(this.opticsRays[2], { x: left.pose.x + bottom.x, y: left.pose.y + bottom.y }, { x: fixed.pose.x, y: fixed.pose.y });
      this.setRaySegment(this.opticsRays[3], { x: fixed.pose.x, y: fixed.pose.y }, { x: right.pose.x, y: right.pose.y - markHeightPx / 2 });
    } else {
      const top = opaqueObjectTopLocal();
      // 光线：光源 → 遮光物顶端 → 影子边界
      this.setRaySegment(this.opticsRays[0], { x: left.pose.x, y: left.pose.y }, { x: fixed.pose.x + top.x, y: fixed.pose.y + top.y });
      this.setRaySegment(
        this.opticsRays[1],
        { x: fixed.pose.x + top.x, y: fixed.pose.y + top.y },
        { x: right.pose.x, y: right.pose.y - markHeightPx },
      );
    }
  }

  /** 光学场景的距离拖拽绑定——两端（左/右）共用同一套逻辑，复用 `beginForceDrag`/
   * `computeDraggedForce`（drag-controller.ts 零改动核心状态机，只多传了一个灵敏度参数）。
   * 松手即提交（不像 `pull_uniform` 那样需要越过临界阈值才提交）——这里是真实空间位置，
   * 松手停在哪就是哪，不存在"没拉动就回零"的物理类比。 */
  private bindOpticsDrag(handle: SVGElement, svg: SVGSVGElement, config: OpticsSceneConfig, side: "left" | "right"): void {
    const varId = side === "left" ? config.leftVar : config.rightVar;
    const actionId = side === "left" ? config.leftAction : config.rightAction;
    // 左端对象画在轴心左侧（leftX = AXIS_X - leftCm*px/cm）：往右拖（远离轨迹起点、靠近固定物）
    // 应该减小距离，和右端"往右拖=增大距离"正好相反方向，用负灵敏度翻转映射。
    const sensitivity = side === "left" ? -OPTICS_DRAG_SENSITIVITY : OPTICS_DRAG_SENSITIVITY;
    let session: ForceDragSession | undefined;
    let liveCm = Number(this.engine.store.variables.get(varId)?.value ?? OPTICS_MIN_DISTANCE_CM);

    const unbind = bindDragSource(handle, svg, {
      onStart: (point) => {
        liveCm = Number(this.engine.store.variables.get(varId)?.value ?? OPTICS_MIN_DISTANCE_CM);
        session = beginForceDrag(point, liveCm);
      },
      onMove: (point) => {
        if (!session) return;
        const raw = computeDraggedForce(session, point, OPTICS_MAX_DISTANCE_CM, sensitivity);
        liveCm = Math.max(OPTICS_MIN_DISTANCE_CM, raw);
        this.updateOpticsVisual(config, side, liveCm);
      },
      onEnd: () => {
        session = undefined;
        this.engine.performAction(actionId, { value: liveCm });
        this.render();
      },
    });
    this.unbindDrags.push(unbind);
  }

  /** 拖拽过程中的增量刷新：重建光学场景的 3 个对象 + 2 条光线，patch 进已挂载的 svgRenderer——
   * 光学场景只有 3 个简单对象，重建成本远低于摩擦力 `updateForceVisual()` 要处理的可变数量砝码，
   * 不需要照抄它"原地 mutate 已有对象"的写法。 */
  private updateOpticsVisual(config: OpticsSceneConfig, side: "left" | "right", liveCm: number): void {
    if (!this.svgRenderer) return;
    const { leftCm: storedLeft, rightCm: storedRight } = this.opticsLeftRightCm(config);
    const leftCm = side === "left" ? liveCm : storedLeft;
    const rightCm = side === "right" ? liveCm : storedRight;
    const objects = this.buildOpticsSnapshot(config, leftCm, rightCm);
    for (const object of objects) this.engine.store.putObject(object);
    this.svgRenderer.sync(objects);
    this.updateOpticsRays(config, objects);
    const liveReadout = this.root.querySelector<HTMLElement>("[data-optics-live] strong");
    if (liveReadout) liveReadout.textContent = liveCm.toFixed(1);
    const liveLabel = this.root.querySelector<HTMLElement>("[data-optics-live-label]");
    if (liveLabel) liveLabel.textContent = side === "left" ? config.leftShortLabel : config.rightShortLabel;
  }

  /** render() 清空 svg 之后才追加放置区矩形，保证它不会被 svg-renderer 的 replaceChildren() 清掉。 */
  private appendDropZone(svg: SVGSVGElement): SVGRectElement {
    const rect = document.createElementNS(SVG_NS, "rect") as SVGRectElement;
    rect.setAttribute("class", "pe-drop-zone");
    rect.setAttribute("data-drop-zone", "");
    rect.setAttribute("x", String(DROP_ZONE.x));
    rect.setAttribute("y", String(DROP_ZONE.y));
    rect.setAttribute("width", String(DROP_ZONE.width));
    rect.setAttribute("height", String(DROP_ZONE.height));
    svg.appendChild(rect);
    return rect;
  }

  /** 当前接触面/砝码数/拉力下的阶段分类——拖拽提交判定/教练文案/木块是否滑出共用同一份计算，
   *  避免各自重复读 store 变量、算法漂移。 */
  private classifyCurrentPull(): PullState {
    const surface = String(this.engine.store.variables.get("surface")?.value ?? "wood");
    const weightCount = Number(this.engine.store.variables.get("weightCount")?.value ?? 0);
    return classifyPull(this.appliedForce, computeNormalForce(weightCount), surface, this.pullStarted);
  }

  /** 木块是否已经"滑出"：本次拖拽已起步（pullStarted），或已记录过一次有效拉动（frictionReading
   *  非空，供 record/conclude 回顾场景复现"木块已被拉出"的最终态）。不再是"拉力超过最大静摩擦就滑出"
   *  的二值判定——起步后即使拉力回落到匀速区间，木块仍应保持滑出（滑动摩擦小于最大静摩擦，一旦动起来
   *  不会自己停回静止位）。 */
  private currentBlockX(): number {
    const moved = this.pullStarted || this.engine.store.variables.get("frictionReading")?.value != null;
    return moved ? BLOCK_REST_X + BLOCK_SLIDE_OFFSET : BLOCK_REST_X;
  }

  /** 拉绳两端的世界坐标：起点=木块挂钩圆环，终点=测力计拉环——都复用 equipment-registry.ts
   * 导出的同一份本地坐标函数，保证绳子永远精确焊在两个图形上，不会跟手位置产生像素级偏差。 */
  private connectorEndpoints(blockX: number): { from: Point; to: Point } {
    const orientation = String(this.engine.store.variables.get("orientation")?.value ?? "flat");
    const hook = blockHookLocal(orientation);
    const from: Point = { x: blockX + hook.x, y: BOARD_Y + hook.y };

    const height = blockHeight(orientation);
    const handle = scaleHandleLocal(this.appliedForce, FORCE_MAX);
    // 与 buildObjectsSnapshot 的测力计定位保持一致（原点 y = BOARD_Y - height/2 - SCALE_HANDLE_Y），
    // 拉环世界 y = 原点 y + handle.y = BOARD_Y - height/2，正好和木块挂钩水平（P0-3）。
    const to: Point = { x: SCALE_ANCHOR_X + handle.x, y: BOARD_Y - height / 2 - SCALE_HANDLE_Y + handle.y };
    return { from, to };
  }

  private setConnectorEndpoints(line: SVGLineElement, blockX: number): void {
    const { from, to } = this.connectorEndpoints(blockX);
    line.setAttribute("x1", String(from.x));
    line.setAttribute("y1", String(from.y));
    line.setAttribute("x2", String(to.x));
    line.setAttribute("y2", String(to.y));
    // 力值 >0 时绳子变粗变实（"绷紧"），松弛/回零时变细——让"这是一根真实的绳"有直觉反馈
    // （修真机反馈"测力计和木块到底有没有连线"，虚线读作参考线、不像实体绳）。
    line.classList.toggle("is-taut", this.appliedForce > 0);
    if (this.connectorDots) {
      this.connectorDots.from.setAttribute("cx", String(from.x));
      this.connectorDots.from.setAttribute("cy", String(from.y));
      this.connectorDots.to.setAttribute("cx", String(to.x));
      this.connectorDots.to.setAttribute("cy", String(to.y));
    }
  }

  /** 木块与测力计之间的拉绳——横跨两个不同的 LabObject，不能用按对象几何模板表达，
   * 照抄放置区矩形"渲染后追加"的既有模式，场景级元素由 scene-manager 直接管理。两个锚点圆环
   * 明确标出绳子焊在哪两个点上，配合实线（不再是虚线）让连接关系一眼可辨。 */
  private appendConnector(svg: SVGSVGElement, blockX: number): SVGLineElement {
    const line = document.createElementNS(SVG_NS, "line") as SVGLineElement;
    line.setAttribute("class", "pe-connector");
    line.setAttribute("data-connector", "");
    const fromDot = document.createElementNS(SVG_NS, "circle") as SVGCircleElement;
    fromDot.setAttribute("class", "pe-connector-anchor");
    fromDot.setAttribute("r", "3");
    const toDot = document.createElementNS(SVG_NS, "circle") as SVGCircleElement;
    toDot.setAttribute("class", "pe-connector-anchor");
    toDot.setAttribute("r", "3");
    this.connectorDots = { from: fromDot, to: toDot };
    this.setConnectorEndpoints(line, blockX);
    svg.appendChild(line);
    svg.appendChild(fromDot);
    svg.appendChild(toDot);
    return line;
  }

  private bindWeightDrag(source: HTMLElement, svg: SVGSVGElement, dropZone: SVGRectElement): void {
    let start: Point | undefined;
    const setZoneReady = (ready: boolean) => dropZone.classList.toggle("is-ready", ready);

    const addWeight = () => {
      this.engine.performAction("add_weight");
      this.render();
    };

    const unbindDrag = bindDragSource(source, svg, {
      onStart: (point) => {
        start = point;
        dropZone.classList.add("is-active");
      },
      onMove: (point) => {
        if (!start) return;
        setZoneReady(hasExceededDragThreshold(start, point) && isPointInRect(point, DROP_ZONE));
      },
      onEnd: (point) => {
        dropZone.classList.remove("is-active", "is-ready");
        const draggedIntoZone = start != null && hasExceededDragThreshold(start, point) && isPointInRect(point, DROP_ZONE);
        start = undefined;
        if (draggedIntoZone) addWeight();
      },
    });

    // 点击兜底：托盘文案承诺"或直接点击"，但 bindDragSource 只挂 pointer 事件、没有 click 监听——
    // 自动化工具/键盘触发时只有 click 没有 pointer 序列，导致"点了没反应"（真机反馈）。拖拽结束时
    // 浏览器不会派发 click（指针发生了移动），因此和上面的 draggedIntoZone 不会重复触发。
    const onClick = () => addWeight();
    source.addEventListener("click", onClick);

    this.unbindDrags.push(() => {
      unbindDrag();
      source.removeEventListener("click", onClick);
    });
  }

  private bindForceDrag(handle: SVGElement, svg: SVGSVGElement): void {
    let session: ForceDragSession | undefined;

    const unbind = bindDragSource(handle, svg, {
      onStart: (point) => {
        this.pullStarted = false;
        session = beginForceDrag(point, this.appliedForce);
      },
      onMove: (point) => {
        if (!session) return;
        this.appliedForce = computeDraggedForce(session, point, FORCE_MAX);
        // 拉力一旦超过最大静摩擦，木块就"起步"了；之后即使回落到匀速区间也保持滑动状态，
        // 匀速判定（classifyPull 的 hasStarted 分支）才成立，否则松手瞬间会被误判成静摩擦阶段。
        if (this.classifyCurrentPull().phase === "starting") this.pullStarted = true;
        this.updateForceVisual();
      },
      onEnd: () => {
        session = undefined;
        // P0-2：只有拉力稳定在滑动摩擦力附近的「匀速」区间才算数，松手才提交；
        // 提交的读数是学生实际稳定出的示数（this.appliedForce），不是 f=μN 的理论值。
        const state = this.classifyCurrentPull();
        if (state.uniform) {
          this.engine.performAction("pull_uniform", { value: this.appliedForce });
          this.render();
        } else {
          // 真实弹簧测力计松手即回零——没匀速就归零，页面自己就能回到初始状态，不用刷新
          // （修真机反馈"拉力超过又放开，要不要回到原点/如何恢复原状态"）。
          this.appliedForce = 0;
          this.pullStarted = false;
          this.updateForceVisual();
        }
      },
    });
    this.unbindDrags.push(unbind);
  }

  /** 拖拽过程中的增量刷新：只 patch 测力计指针/读数 + 木块是否"滑出"的视觉反馈，不整棵重建。 */
  private updateForceVisual(): void {
    if (!this.svgRenderer) return;
    const weightCount = Number(this.engine.store.variables.get("weightCount")?.value ?? 0);
    const blockX = this.currentBlockX();

    const block = this.engine.store.getObject("block");
    const scale = this.engine.store.getObject("spring-scale");
    if (block) block.pose = { ...block.pose, x: blockX };
    if (scale) scale.state = { ...scale.state, appliedForce: this.appliedForce };
    const weights: LabObject[] = [];
    for (let i = 0; i < weightCount; i++) {
      const weight = this.engine.store.getObject(`weight-${i}`);
      if (!weight) continue;
      weight.pose = { ...weight.pose, x: blockX + 18 };
      weights.push(weight);
    }
    const objects = [block, scale, ...weights].filter((object): object is LabObject => object != null);
    this.svgRenderer.sync(objects);
    if (this.connectorLine) this.setConnectorEndpoints(this.connectorLine, blockX);

    const coach = this.root.querySelector<HTMLElement>("[data-coach]");
    if (coach) coach.textContent = this.coachText();
    const liveReadout = this.root.querySelector<HTMLElement>("[data-force-readout] strong");
    if (liveReadout) liveReadout.textContent = this.appliedForce.toFixed(1);
  }

  private coachText(): string {
    if (this.appliedForce >= FORCE_MAX) return COACH_TEXT["blocked-range"];
    return COACH_TEXT[this.classifyCurrentPull().phase];
  }

  /** "开始拉动"按钮的可达性兜底路径：不再瞬间跳转，先把力值拉到匀速区间的稳定示数、播完滑出动画，
   *  再推进步骤，让按钮路径和真实拖拽松手提交时的视觉体验一致（修真机反馈"点了就跳页，没有任何拉动
   *  的感觉"）。提交的是滑动摩擦力理论值取整到 0.1N 的稳定读数，与测力计 0.1N 分度一致。 */
  private commitPullWithFeedback(button: HTMLButtonElement): void {
    button.disabled = true;
    this.pullStarted = true;
    const classified = this.classifyCurrentPull();
    const reading = Math.round(classified.kineticFriction * 10) / 10;
    this.appliedForce = reading;
    this.updateForceVisual();
    setTimeout(() => {
      this.engine.performAction("pull_uniform", { value: reading });
      this.render();
    }, PULL_FEEDBACK_DELAY_MS);
  }

  /** record 步骤的控制区：点击前先说明按钮的作用，点击后把 `store.trials()` 已经记下的每一行
   * 用人话回显出来（真机反馈"不知道做什么的，没有及时反馈"——之前唯一可观察的变化只是
   * "已记录 X/N 组"里的数字，看不出到底记了什么）。 */
  /** `stageId` 由调用方传入（而不是内部读 `engine.currentStage.id`）——反馈冻结态下引擎已经
   * advance 到下一个 stage，取错 stageId 会查到空数据表（067 §M3）。`feedback` 存在时"记录当前
   * 数据"按钮禁用，避免反馈横幅展示期间还能再记一组。 */
  private renderRecordControls(step: StepDefinition, stageId: string, feedback?: PendingFeedback): string {
    const required = step.requiredTrials ?? 1;
    const rows = this.engine.store.trials(stageId);
    const rowsHtml = rows.length
      ? `<ol class="pe-trial-rows">${rows.map((row, index) => `<li>第 ${index + 1} 组：${this.formatTrialRow(row)}</li>`).join("")}</ol>`
      : "";
    return `
      <p class="pe-record-hint">点击下方按钮，会把此刻测力计等读数存成一行记录，凑够 ${required} 组后自动进入下一步。</p>
      <p class="pe-trials">已记录 ${rows.length} / ${required} 组数据</p>
      ${rowsHtml}
      <button data-action="record"${feedback ? " disabled" : ""}>记录当前数据</button>
      ${!feedback && this.blockedNotice ? `<button class="pe-return-act" data-action="return-to-act">返回操作台，调整条件</button>` : ""}
    `;
  }

  /** 把一行数据表记录拼成人话——字段的中文名/单位直接读 `mission.json` 里变量自带的
   * `label`/`unit`（本来就有，不用另造一份文案），`surface`/`orientation` 是 id，复用已有的
   * label 映射函数。 */
  private formatTrialRow(row: TrialRow): string {
    return Object.entries(row)
      .map(([fieldId, value]) => {
        const label = this.engine.store.variables.get(fieldId)?.label ?? fieldId;
        return `${escapeHtml(label)} ${escapeHtml(this.formatFieldValue(fieldId, value))}`;
      })
      .join("，");
  }

  private formatFieldValue(fieldId: string, value: VariableValue): string {
    if (fieldId === "surface") return surfaceLabel(String(value));
    if (fieldId === "orientation") return orientationLabel(String(value));
    if (typeof value === "number") {
      const unit = this.engine.store.variables.get(fieldId)?.unit ?? "";
      return unit ? `${value}${unit}` : String(value);
    }
    return String(value ?? "");
  }

  /** 知识卡的标题+正文——确认屏答错时也要展示同样的内容，但不带"继续"按钮（确认屏走自己的
   * "下一步"按钮，不需要一张可独立关闭的卡片）。 */
  private renderKnowledgeBody(item: KnowledgeItem): string {
    return `<h4>💡 ${escapeHtml(item.title)}</h4><p>${escapeHtml(item.body)}</p>`;
  }

  private renderKnowledgeCard(item: KnowledgeItem): string {
    return `
      <div class="pe-knowledge">
        ${this.renderKnowledgeBody(item)}
        <button data-action="dismiss-knowledge">继续</button>
      </div>
    `;
  }

  /** 提交后的原地反馈条——追加在冻结的 step 视图末尾（`render()` 里的 `feedback` 分支），
   * 不再整屏替换 root（067 §M3，替代原来的 `renderConfirmScreen()`；真机第六轮反馈"回答后
   * 没有确认对错就直接跳下一题"）。回答正确时也一律回显题干/选中项文案——原
   * `renderConfirmScreen()` 的 `correct===true` 分支 body 是空字符串，只有标题和按钮，
   * 用户看不出"刚才回答了什么"（Q3 修复：`docs/111/010` 评审后用户确认）。 */
  private renderFeedbackBanner(feedback: PendingFeedback): string {
    const heading =
      feedback.recordSummary !== undefined
        ? "✅ 数据记录完成"
        : feedback.correct === true
          ? "✅ 回答正确！"
          : feedback.correct === false
            ? "❌ 再想一想"
            : "📝 已记录你的想法";
    const selectedOption = feedback.selectedOptionId
      ? (feedback.step.options ?? feedback.step.exam?.options ?? []).find((option) => option.id === feedback.selectedOptionId)
      : undefined;
    const echo = selectedOption
      ? `<p class="pe-feedback-echo">你的回答：${escapeHtml(selectedOption.label)}</p>`
      : feedback.recordSummary
        ? `<p class="pe-feedback-echo">${escapeHtml(feedback.recordSummary)}</p>`
        : "";
    const knowledge = feedback.knowledge ? `<div class="pe-knowledge">${this.renderKnowledgeBody(feedback.knowledge)}</div>` : "";
    const nextLabel = feedback.crossStage ? "进入下一个实验 →" : "下一步 →";
    const stateClass =
      feedback.recordSummary !== undefined
        ? "pe-feedback--record"
        : feedback.correct === true
          ? "pe-feedback--correct"
          : feedback.correct === false
            ? "pe-feedback--incorrect"
            : "pe-feedback--neutral";
    return `
      <div class="pe-feedback ${stateClass}">
        <p class="pe-feedback-heading">${heading}</p>
        ${echo}
        ${knowledge}
        <button class="pe-feedback-next" data-action="feedback-advance">${nextLabel}</button>
      </div>
    `;
  }

  private renderCompletion(): string {
    const title = this.engine.pack?.experiment.title ?? "";
    return `
      <div class="pe-shell pe-completion">
        <h2>🎉 实验全部完成</h2>
        <p>你已经完成「${escapeHtml(title)}」的全部子实验。</p>
      </div>
    `;
  }

  private bindEvents(): void {
    this.root.querySelectorAll<HTMLButtonElement>("[data-action='submit-choice']").forEach((button) => {
      button.addEventListener("click", () => {
        const optionId = button.dataset.optionId;
        if (!optionId) return;
        const beforeStage = this.engine.currentStage;
        const beforeStep = this.engine.currentStep; // advance 后就读不到了，提交前先存一份
        this.awaitingConfirm = true;
        let result: ReturnType<typeof this.engine.submitChoice>;
        try {
          result = this.engine.submitChoice(optionId);
        } finally {
          this.awaitingConfirm = false;
        }
        // 整个 mission 走完时 advance() 的终局分支不会 emit step.started（只 emit
        // experiment.completed），"completed" 监听器已经同步画出完成页——这里必须让开，
        // 不然会被下面的反馈横幅用"进入下一个实验"覆盖掉（回归：完成页被错误确认屏盖住的 bug）。
        if (result.completed) return;
        // guess/design/conclude 从不阻断（`checkChoiceStep` 恒为 passed，见 mission-engine.ts
        // 类注释），`submitChoice()` 的 `advanced` 恒为 true，这里不需要 blocked 分支。
        const knowledge = this.confirmKnowledge;
        this.confirmKnowledge = undefined;
        this.pendingFeedback = {
          stage: beforeStage,
          step: beforeStep,
          selectedOptionId: optionId,
          correct: result.correct,
          knowledge,
          crossStage: result.nextStageId !== beforeStage.id,
        };
        this.render();
      });
    });
    this.root.querySelectorAll<HTMLButtonElement>("[data-action='submit-exam']").forEach((button) => {
      button.addEventListener("click", () => {
        const optionId = button.dataset.optionId;
        if (!optionId) return;
        const result = this.engine.submitExam(optionId);
        this.examResult = result.correct === true ? "✓ 回答正确" : result.correct === false ? "✗ 再想一想" : "";
        this.render();
      });
    });
    this.root.querySelectorAll<HTMLButtonElement>("[data-action='submit-exam-step']").forEach((button) => {
      button.addEventListener("click", () => {
        const optionId = button.dataset.optionId;
        if (!optionId) return;
        const beforeStage = this.engine.currentStage;
        const beforeStep = this.engine.currentStep;
        this.awaitingConfirm = true;
        let result: ReturnType<typeof this.engine.submitExam>;
        try {
          result = this.engine.submitExam(optionId);
        } finally {
          this.awaitingConfirm = false;
        }
        if (result.completed) return;
        const knowledge = this.confirmKnowledge;
        this.confirmKnowledge = undefined;
        this.pendingFeedback = {
          stage: beforeStage,
          step: beforeStep,
          selectedOptionId: optionId,
          correct: result.correct,
          knowledge,
          crossStage: result.nextStageId !== beforeStage.id,
        };
        this.render();
      });
    });
    this.root.querySelector<HTMLButtonElement>("[data-action='submit-summary']")?.addEventListener("click", () => {
      this.awaitingConfirm = true;
      let result: ReturnType<typeof this.engine.submitSummary>;
      try {
        result = this.engine.submitSummary();
      } finally {
        this.awaitingConfirm = false;
      }
      if (result.completed) return;
      this.render();
    });
    this.root.querySelector<HTMLButtonElement>("[data-action='submit-knowledge']")?.addEventListener("click", () => {
      this.awaitingConfirm = true;
      let result: ReturnType<typeof this.engine.submitKnowledge>;
      try {
        result = this.engine.submitKnowledge();
      } finally {
        this.awaitingConfirm = false;
      }
      if (result.completed) return;
      this.render();
    });
    this.root.querySelectorAll<HTMLButtonElement>("[data-action='perform']").forEach((button) => {
      button.addEventListener("click", () => {
        const actionId = button.dataset.actionId as PhysicsActionId | undefined;
        if (!actionId) return;
        if (actionId === "pull_uniform") {
          this.commitPullWithFeedback(button);
          return;
        }
        const payload: ActionPayload = {};
        if (button.dataset.surface) payload.surfaceId = button.dataset.surface;
        if (button.dataset.orientation) payload.orientation = button.dataset.orientation as "flat" | "vertical";
        this.engine.performAction(actionId, payload);
        this.render();
      });
    });
    this.root.querySelectorAll<HTMLButtonElement>("[data-action='optics-step']").forEach((button) => {
      button.addEventListener("click", () => {
        const actionId = button.dataset.actionId as PhysicsActionId | undefined;
        const varId = actionId ? opticsVarForAction(actionId) : undefined;
        if (!actionId || !varId) return;
        const delta = Number(button.dataset.delta ?? 0);
        const current = Number(this.engine.store.variables.get(varId)?.value ?? OPTICS_MIN_DISTANCE_CM);
        const next = Math.min(OPTICS_MAX_DISTANCE_CM, Math.max(OPTICS_MIN_DISTANCE_CM, current + delta));
        this.engine.performAction(actionId, { value: next });
        this.render();
      });
    });
    this.root.querySelector<HTMLButtonElement>("[data-action='record']")?.addEventListener("click", () => {
      const beforeStage = this.engine.currentStage;
      const beforeStep = this.engine.currentStep;
      this.awaitingConfirm = true;
      let result: ReturnType<typeof this.engine.recordTrial>;
      try {
        result = this.engine.recordTrial();
      } finally {
        this.awaitingConfirm = false;
      }
      // 同上：mission 在 record 步骤收尾时（配置驱动的其他 mission 有可能这样排布）也要让开
      // 完成页，不硬编码假设 record 不可能是最后一步。
      if (result.completed) return;
      if (result.advanced) {
        const rows = this.engine.store.trials(beforeStage.id);
        const recordSummary = rows.map((row, index) => `第 ${index + 1} 组：${this.formatTrialRow(row)}`).join("；");
        this.pendingFeedback = {
          stage: beforeStage,
          step: beforeStep,
          recordSummary,
          crossStage: result.nextStageId !== beforeStage.id,
        };
        this.render();
      } else {
        this.render();
      }
    });
    this.root.querySelector<HTMLButtonElement>("[data-action='return-to-act']")?.addEventListener("click", () => {
      // 回环回操作步骤会同步 emit step.started——借用 awaitingConfirm 只是为了压掉监听器内部
      // 那次 render()，避免同一次点击重复重建 SVG 场景/拖拽绑定两遍；这不是一次"完成一步"，
      // 不需要走确认屏。
      this.awaitingConfirm = true;
      try {
        this.engine.returnToAct();
      } finally {
        this.awaitingConfirm = false;
      }
      this.render();
    });
    this.root.querySelector<HTMLButtonElement>("[data-action='hint']")?.addEventListener("click", () => {
      this.engine.requestHint();
      this.render();
    });
    this.root.querySelector<HTMLButtonElement>("[data-action='dismiss-knowledge']")?.addEventListener("click", () => {
      this.activeKnowledge = undefined;
      this.render();
    });
    this.root.querySelector<HTMLButtonElement>("[data-action='feedback-advance']")?.addEventListener("click", () => {
      this.pendingFeedback = undefined;
      this.render();
    });
  }

  private injectStyles(): void {
    if (document.getElementById("pe-styles")) return;
    const style = document.createElement("style");
    style.id = "pe-styles";
    style.textContent = `
      :root {
        /* ── 设计 tokens：拟物实验室风（todo/063 P0）── 浅木色实验台质感，替代原后台蓝紫风 */
        --pe-bg-page: linear-gradient(180deg, #f7f2e9 0%, #f1e8d8 100%);
        --pe-bg-card: #fffdf8;
        --pe-bg-stage: linear-gradient(180deg, #efe3ca 0%, #e3d3ac 100%);
        --pe-primary: #3a7ca5;
        --pe-primary-dark: #2c5f80;
        --pe-accent: #fb7185;
        --pe-accent-dark: #e2455a;
        --pe-success: #4d8b31;
        --pe-danger: #c0392b;
        --pe-warning: #d98c1f;
        --pe-ink: #2f2416;
        --pe-ink-soft: #6b5c46;
        --pe-tint-info-bg: #e3f0f7;
        --pe-tint-info-text: #205a7a;
        --pe-tint-success-bg: #e8f1de;
        --pe-tint-success-text: #3f6b2a;
        --pe-tint-warning-bg: #fbeedd;
        --pe-tint-warning-text: #8a5a1f;
        --pe-tint-danger-bg: #fbe4e1;
        --pe-tint-danger-text: #8a2f22;
        --pe-tint-accent-bg: #fde6e9;
        --pe-tint-accent-text: #a83a48;
        --pe-rope: #78350f;
        --pe-font: "MiSans", "HarmonyOS Sans SC", "PingFang SC", system-ui, -apple-system, sans-serif;
        --pe-radius-card: 20px;
        --pe-radius-btn: 12px;
        --pe-radius-pill: 999px;
        --pe-shadow-card: 0 8px 24px rgba(90, 62, 20, 0.12);
        --pe-shadow-hover: 0 10px 26px rgba(90, 62, 20, 0.18);
      }
      body { font-family: var(--pe-font); background: var(--pe-bg-page); margin: 0; color: var(--pe-ink); }
      .pe-shell { max-width: 640px; margin: 32px auto; padding: 24px; background: var(--pe-bg-card); border-radius: var(--pe-radius-card); box-shadow: var(--pe-shadow-card); }
      /* act 步骤专属加宽，容纳左右分栏（todo/064 方案 B）；其余 step kind 维持 640px 单列不变。 */
      .pe-shell--act { max-width: 900px; }

      /* 顶部两级进度：文字标签 + 九步链胶囊条（todo/063 P0-02，替代原 ●○ 圆点） */
      .pe-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; color: var(--pe-ink-soft); font-size: 13px; margin-bottom: 14px; }
      .pe-header-label { flex-shrink: 0; font-weight: 600; }
      .pe-pills { display: flex; gap: 4px; overflow-x: auto; scrollbar-width: none; }
      .pe-pills::-webkit-scrollbar { display: none; }
      .pe-pill { flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: var(--pe-radius-pill); font-size: 11px; font-weight: 700; background: #ece1ca; color: #b3a37f; transition: transform 150ms ease-out, background 150ms ease-out, color 150ms ease-out; }
      .pe-pill--done { background: var(--pe-tint-success-bg); color: var(--pe-success); }
      .pe-pill--current { background: var(--pe-primary); color: #fff; transform: scale(1.2); box-shadow: 0 2px 6px rgba(58,124,165,0.4); }

      .pe-badge { display: inline-block; margin: 0 0 4px; padding: 2px 10px; border-radius: var(--pe-radius-pill); font-size: 12px; font-weight: 600; }
      .pe-badge-question { background: var(--pe-tint-info-bg); color: var(--pe-tint-info-text); }
      .pe-badge-task { background: var(--pe-tint-success-bg); color: var(--pe-tint-success-text); }

      /* 提问区：探究问题 + 本步任务，浅色卡片，和下方操作区分层（todo/063 P0-03） */
      .pe-question-panel { padding: 16px 18px; margin-bottom: 16px; background: linear-gradient(180deg, #fbf7ee 0%, #f6efdd 100%); border-radius: 14px; border: 1px solid #ecdfc2; }
      .pe-question { margin: 4px 0 14px; font-size: 21px; line-height: 1.35; }
      .pe-step-meta { padding-top: 10px; border-top: 1px dashed #e2d2ab; }
      .pe-prompt { margin: 0 0 8px; }

      /* 操作区：选项/实验台/按钮（todo/063 P0-03/04） */
      .pe-step { margin-top: 4px; }
      .pe-options { display: flex; flex-direction: column; gap: 10px; margin: 12px 0; }
      /* 所有壳内按钮统一走卡片基调（用 descendant 选择器覆盖 record/return-act 等非直接子节点，
         todo/063 排查发现原 .pe-shell > button 只匹配直接子节点，记录/返回按钮曾经吃不到样式）。 */
      .pe-shell button {
        padding: 13px 18px; border-radius: var(--pe-radius-btn); border: 1.5px solid #e4d7ba; background: var(--pe-bg-card);
        cursor: pointer; font-size: 15px; text-align: left; font-family: var(--pe-font); color: var(--pe-ink);
        box-shadow: 0 2px 6px rgba(90,62,20,0.06); transition: transform 150ms ease-out, box-shadow 150ms ease-out, border-color 150ms ease-out, background 150ms ease-out;
      }
      .pe-shell button:hover { transform: translateY(-2px); box-shadow: var(--pe-shadow-hover); border-color: var(--pe-primary); background: #f4f9fc; }
      /* 反馈冻结态：选项禁用后不再响应悬浮位移，靠颜色（下方 .pe-option--*）标出选中/对错（067 §M3）。 */
      .pe-shell button:disabled { cursor: default; opacity: 0.7; }
      .pe-shell button:disabled:hover { transform: none; box-shadow: 0 2px 6px rgba(90,62,20,0.06); border-color: #e4d7ba; background: var(--pe-bg-card); }
      button.pe-option--selected:disabled { opacity: 1; border-color: var(--pe-primary); background: #f4f9fc; }
      button.pe-option--correct:disabled { opacity: 1; border-color: var(--pe-success); background: var(--pe-tint-success-bg); color: var(--pe-tint-success-text); }
      button.pe-option--wrong:disabled { opacity: 1; border-color: var(--pe-danger); background: var(--pe-tint-danger-bg); color: var(--pe-tint-danger-text); }
      .pe-actions { display: flex; flex-wrap: wrap; gap: 10px; margin: 12px 0; }
      /* 主 CTA（记录/继续等）统一高对比主色按钮，和普通选项区分开（todo/063 P0-04）。
         选择器特异性与上面的 .pe-shell button 打平，靠源码顺序（写在后面）覆盖共享属性。
         pull_uniform 不在此列——它已降级为场景下方的低调备选路径（todo/064 方案 D），走 .pe-fallback-pull。 */
      button[data-action='record'], button[data-action='submit-knowledge'],
      button[data-action='submit-summary'], button[data-action='submit-exam'], button[data-action='submit-exam-step'],
      button[data-action='dismiss-knowledge'] {
        background: var(--pe-primary); border-color: var(--pe-primary); color: #fff; font-weight: 600; text-align: center;
      }
      button[data-action='record']:hover, button[data-action='submit-knowledge']:hover,
      button[data-action='submit-summary']:hover, button[data-action='submit-exam']:hover, button[data-action='submit-exam-step']:hover,
      button[data-action='dismiss-knowledge']:hover {
        background: var(--pe-primary-dark); border-color: var(--pe-primary-dark); transform: translateY(-2px); box-shadow: var(--pe-shadow-hover);
      }
      /* "开始拉动"降级为低调文字按钮，仿照下面 .pe-hint 的样式套路（todo/064 方案 D）。 */
      button.pe-fallback-pull { margin-top: 8px; padding: 0; background: none; border: none; box-shadow: none; color: var(--pe-ink-soft); font-size: 13px; cursor: pointer; text-decoration: underline; text-align: left; }
      button.pe-fallback-pull:hover { transform: none; box-shadow: none; background: none; color: var(--pe-primary-dark); }
      .pe-readout { font-size: 18px; font-weight: 600; color: var(--pe-ink); }
      .pe-record-hint { color: var(--pe-ink-soft); font-size: 13px; margin: 0 0 6px; }
      .pe-trials { color: var(--pe-ink-soft); }
      .pe-trial-rows { margin: 4px 0 10px; padding-left: 20px; color: var(--pe-ink-soft); font-size: 14px; }
      .pe-trial-rows li:last-child { animation: pe-row-flash 900ms ease-out; border-radius: 4px; }
      @keyframes pe-row-flash {
        0% { background: var(--pe-tint-success-bg); }
        100% { background: transparent; }
      }
      .pe-blocked { color: var(--pe-tint-warning-text); background: var(--pe-tint-warning-bg); padding: 10px 14px; border-radius: var(--pe-radius-btn); }
      .pe-knowledge { margin-top: 16px; padding: 14px 16px; background: var(--pe-tint-info-bg); border-radius: 14px; border: 1px solid #c7dfec; }
      .pe-knowledge--primer { margin: 0 0 16px; }
      .pe-exam { margin-top: 16px; padding: 14px 16px; background: var(--pe-tint-accent-bg); border-radius: 14px; }
      .pe-exam-result { font-weight: 600; }
      .pe-summary-item { margin: 12px 0; padding: 12px 14px; background: #faf6ea; border-radius: var(--pe-radius-btn); border: 1px solid #ecdfc2; }
      .pe-summary-item h4 { margin: 0 0 6px; }
      .pe-summary-item ul { margin: 0; padding-left: 20px; color: var(--pe-ink-soft); }
      /* button. 前缀把特异性打到和 .pe-shell button 一致，源码顺序在其后，靠顺序覆盖共享属性
         （单独 class 选择器特异性更低，会被上面的基础卡片按钮样式盖掉，todo/063 排查发现的隐藏问题）。 */
      button.pe-hint { margin-top: 16px; padding: 0; background: none; border: none; box-shadow: none; color: var(--pe-primary); cursor: pointer; font-weight: 600; }
      button.pe-hint:hover { transform: none; box-shadow: none; background: none; text-decoration: underline; }
      .pe-completion { text-align: center; }

      /* act 步骤左右分栏（todo/064 方案 B）：左「实验工作台」+ 右「实验任务」checklist，
         720px 以下折叠成单列且任务面板前置，呼应 demo/phy-lab-product-ui.html 的响应式规则。 */
      .pe-act-layout { display: grid; grid-template-columns: minmax(0,1fr) 240px; gap: 16px; align-items: start; }
      @media (max-width: 720px) {
        .pe-act-layout { grid-template-columns: 1fr; }
        .pe-task-col { order: -1; }
      }
      .pe-task-col { padding: 14px 16px; background: #faf6ea; border: 1px solid #ecdfc2; border-radius: 14px; }
      .pe-task-col-title { margin: 0 0 10px; font-size: 15px; }
      .pe-task-checklist { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 14px; }
      .pe-task-item { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; color: var(--pe-ink-soft); }
      .pe-task-num { flex-shrink: 0; width: 20px; height: 20px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; background: #ece1ca; color: #b3a37f; }
      .pe-task-body { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
      .pe-task-label { font-size: 13px; }
      .pe-task-desc { margin: 0; font-size: 12px; line-height: 1.5; color: var(--pe-ink-soft); }
      .pe-task-item--active { color: var(--pe-primary-dark); font-weight: 600; }
      .pe-task-item--active .pe-task-num { background: var(--pe-primary); color: #fff; }
      .pe-task-item--active .pe-task-desc { color: var(--pe-primary-dark); font-weight: 500; }
      .pe-task-item--done { color: var(--pe-success); }
      .pe-task-item--done .pe-task-num { background: var(--pe-tint-success-bg); color: var(--pe-success); }

      /* 场景下方结果条：实时拉力 + 已记录示数，追加修复把"当前拉力"从场景上方挪到下方——
         先看实验本身，再看数据结果（todo/064 追加修复）。 */
      .pe-result-row { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin-top: 10px; }
      .pe-scene-wrap { margin: 0; }
      /* 实验台桌面质感：暖木色渐变替代原灰蓝平底（todo/063 P0-01），几何器材重绘留给 P1 */
      .pe-scene { width: 100%; height: auto; aspect-ratio: 640 / 300; background: var(--pe-bg-stage); border-radius: 14px; border: 1px solid #d8c69c; box-shadow: inset 0 2px 8px rgba(90,62,20,0.1); touch-action: none; }
      .pe-scene [data-object-type="block"] { transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1); }
      .pe-scene [data-object-type="weight"] { transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1); }
      .pe-scene [data-handle] { cursor: grab; animation: pe-handle-pulse 1.6s ease-in-out infinite; transition: cx 200ms ease-out, cy 200ms ease-out, x 200ms ease-out, y 200ms ease-out, fill 200ms ease-out; }
      @keyframes pe-handle-pulse {
        0%, 100% { filter: drop-shadow(0 0 0 rgba(251,113,133,0)); }
        50% { filter: drop-shadow(0 0 5px rgba(251,113,133,0.65)); }
      }
      .pe-drop-zone { fill: transparent; stroke: #a3906a; stroke-width: 2; stroke-dasharray: 6 6; opacity: 0.4; pointer-events: none; }
      .pe-drop-zone.is-active { opacity: 1; }
      .pe-drop-zone.is-ready { stroke: var(--pe-success); fill: rgba(77,139,49,0.12); }
      .pe-weight-tray { display: inline-block; margin: 8px 0; padding: 9px 16px; border-radius: var(--pe-radius-btn); border: 1.5px dashed #b9a888; background: #f4ecda; cursor: grab; user-select: none; font-size: 14px; color: var(--pe-ink); }
      .pe-scene-wrap--recap { margin-top: 4px; }
      .pe-recap-caption { color: var(--pe-ink-soft); font-size: 13px; margin: 0 0 6px; }
      .pe-scene-readonly { pointer-events: none; opacity: 0.92; }
      .pe-scene-readonly [data-handle] { animation: none; cursor: default; }
      .pe-connector { stroke: var(--pe-rope); stroke-width: 2; opacity: 0.85; transition: x1 200ms ease-out, y1 200ms ease-out, x2 200ms ease-out, y2 200ms ease-out, stroke-width 150ms ease-out, opacity 150ms ease-out; }
      .pe-connector.is-taut { stroke-width: 3.5; opacity: 1; }
      .pe-connector-anchor { fill: var(--pe-rope); stroke: #fff; stroke-width: 1; }
      /* 场景里的连线/锚点/量程轨道等都是纯装饰，几何模板按 z 序叠加时可能盖住拖拽热点
         （真实浏览器里点不中拉环、jsdom 因为不做真实命中测试从没测出来）——默认全场景不接收指针事件，
         只把拉环（唯一的拖拽热点）单独放开，且只在可交互场景放开（只读回顾场景禁止拖拽）。 */
      .pe-scene * { pointer-events: none; }
      .pe-scene:not(.pe-scene-readonly) [data-handle] { pointer-events: auto; }
      .pe-force-live { display:inline-block; margin:4px 0 8px; padding:5px 16px; border-radius: var(--pe-radius-pill); background:#fbeedd; border:1px solid #eccb98; font-size:15px; color: var(--pe-tint-warning-text); }
      .pe-force-live strong { font-size:20px; }
      /* 光学场景（todo/068）：光线示意线 + 拖拽兜底步进按钮，复用 .pe-fallback-pull 的低调文字按钮观感。 */
      .pe-ray { stroke: #f59e0b; stroke-width: 1.5; stroke-dasharray: 4 3; opacity: 0.75; pointer-events: none; transition: x1 200ms ease-out, y1 200ms ease-out, x2 200ms ease-out, y2 200ms ease-out; }
      .pe-optics-fallback { display: flex; flex-direction: column; gap: 4px; margin-top: 6px; }
      .pe-optics-stepper { display: flex; align-items: center; gap: 10px; font-size: 13px; color: var(--pe-ink-soft); }
      .pe-optics-stepper button.pe-fallback-pull { margin-top: 0; }

      /* 原地反馈条：追加在冻结 step 视图末尾，不再整屏切换（067 §M3，替代原 .pe-confirm*）。 */
      .pe-feedback { margin-top: 16px; padding: 16px 18px; border-radius: 14px; text-align: left; }
      .pe-feedback-heading { margin: 0 0 8px; font-size: 20px; font-weight: 700; }
      .pe-feedback-echo { margin: 0 0 8px; color: var(--pe-ink-soft); background: #faf6ea; border-radius: var(--pe-radius-btn); padding: 10px 14px; }
      .pe-feedback--correct { background: linear-gradient(180deg, #eef6e6 0%, var(--pe-bg-card) 55%); border: 1px solid #d3e8c4; }
      .pe-feedback--correct .pe-feedback-heading { color: var(--pe-success); }
      .pe-feedback--incorrect { background: linear-gradient(180deg, #fcecea 0%, var(--pe-bg-card) 55%); border: 1px solid #f3cfc9; }
      .pe-feedback--incorrect .pe-feedback-heading { color: var(--pe-danger); }
      .pe-feedback--record { background: linear-gradient(180deg, #e9f2f7 0%, var(--pe-bg-card) 55%); border: 1px solid #c7dfec; }
      .pe-feedback--record .pe-feedback-heading { color: var(--pe-primary-dark); }
      .pe-feedback--neutral { background: #faf6ea; border: 1px solid #ecdfc2; }
      .pe-feedback--neutral .pe-feedback-heading { color: var(--pe-ink); }
      button.pe-feedback-next { margin-top: 8px; padding: 12px 24px; border: none; background: var(--pe-primary); color: #fff; font-size: 15px; font-weight: 600; cursor: pointer; box-shadow: var(--pe-shadow-card); transition: transform 150ms ease-out, background 150ms ease-out; text-align: center; }
      button.pe-feedback-next:hover { background: var(--pe-primary-dark); transform: translateY(-2px); box-shadow: var(--pe-shadow-card); }
      button.pe-return-act { margin-top: 8px; background: var(--pe-tint-warning-bg); border-color: #eccb98; color: var(--pe-tint-warning-text); }

      /* 移动端专项（todo/063 P0-06）：收紧留白/字号，实验台保留最小可见高度，进度胶囊可横滑 */
      @media (max-width: 480px) {
        .pe-shell { margin: 0 auto; padding: 16px; border-radius: 0; max-width: 100%; }
        .pe-question-panel { padding: 12px 14px; }
        .pe-question { font-size: 18px; margin-bottom: 10px; }
        .pe-prompt { font-size: 12.5px; line-height: 1.4; }
        .pe-act-layout { gap: 12px; }
        .pe-scene { min-height: 200px; }
      }
    `;
    document.head.appendChild(style);
  }
}
